import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../../header";
import Navvbar from "../../Navvbar";
import axios from "axios";

import EditSubscriptions from "./editSubscriptions";
import EditNotifications from "./editNotifications";
import EditPreferances from "./editPreferences";
import EditAuthentication from "./editauthentication";
import EditProductivity from "./editProductivity";
import EditPermission from "./editPermission";
import EditLabDetails from "./editLabDetails";

import { useAuth } from "../../../login/AuthProvider.js";

// =========================================
// CSS IMPORT (alag file)
// =========================================
import "./editUser.css";

// =========================================
// SIDEBAR (isi file ke andar, alag import nahi)
// =========================================
const Sidebar = () => {
  return (
    <div className="my-sidebar">
      <h2 className="my-sidebar-title">Settings</h2>
      <div className="my-sidebar-links">
        <Link to="/lab-details" className="my-sidebar-link">Lab Details</Link>
        <Link to="#" onClick={(e) => e.preventDefault()} className="my-sidebar-link my-sidebar-disabled">Store</Link>
        <Link to="/users" className="my-sidebar-link">Users</Link>
        <Link to="#" onClick={(e) => e.preventDefault()} className="my-sidebar-link my-sidebar-disabled">Permission Groups</Link>
        <Link to="/accountTable" className="my-sidebar-link">Accounts</Link>
        <Link to="/financeOwerview" className="my-sidebar-link">Finances</Link>
        <Link to="/owersetting" className="my-sidebar-link">Case Settings</Link>
      </div>
    </div>
  );
};

const EditUser = () => {
  const { userId } = useParams();
  const { user, getAuthHeaders, getApiBaseUrl } = useAuth();
  const toastIdRef = useRef(null);

  // Get userRole from localStorage
  const userRole = localStorage.getItem("userRole");

  // Check if current user is admin
  const isAdmin = userRole === "Admin";

  const [state, setState] = useState({
    salutation: "",
    first_name: "",
    last_name: "",
    middleInitial: "",
    userId: "",
    email: "",
    username: "",
    labEmail: "",
    labName: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    telephone: "",
    timeZone: "UTC",
    subdomain: "",
    services: [],
    newsletter: false,
    trainingInterest: false,
    emailNotifications: {},
    webNotifications: {},
    preferences: {
      teethNotation: "Universal",
      language: "English (US)",
      disableRxTyping: false,
      technicianStamp: "",
    },
    productivity: {},
    permissions: {},
    authentication: {
      id: "",
      userName: "",
      password: "",
      confirmUserName: "",
      currentPasswordMask: "********",
      confirmPassword: "",
      noEmail: false,
      firstName: "",
      lastName: "",
    },
    labDetails: {
      labName: "",
      labEmail: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      telephone: "",
      subdomain: "",
    },
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // =========================================================
  // CUSTOM TOAST REPLACEMENT (Using simple alert to avoid bug)
  // =========================================================
  const showToast = (message, type = "success") => {
    if (type === "error") {
      alert(`Error: ${message}`);
    } else if (type === "warning") {
      alert(`Warning: ${message}`);
    } else {
      alert(message);
    }
  };

  // =========================================================
  // FETCH USER DATA
  // =========================================================

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!userId) {
          throw new Error("User ID not found in URL");
        }

        const token = localStorage.getItem("accessToken");

        if (!token) {
          throw new Error(
            "No authentication token found. Please login again."
          );
        }

        const response = await axios.get(
          `${getApiBaseUrl()}/api/auth/users/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          const userData = response.data.user;

          const fullName = `${userData.first_name || ""} ${
            userData.last_name || ""
          }`.trim();

          sessionStorage.setItem("username", fullName);

          localStorage.setItem("userEmail", userData.email || "");

          const noEmail = !!userData.username && !userData.email;

          const userName = noEmail
            ? userData.username
            : userData.email;

          setState((prev) => ({
            ...prev,
            ...userData,

            emailNotifications:
              userData.emailNotifications || {},

            webNotifications:
              userData.webNotifications || {},

            authentication: {
              ...prev.authentication,

              id: userData.userId?.toString() || "",

              userName,

              confirmUserName: userName,

              noEmail,

              firstName: userData.first_name || "",

              lastName: userData.last_name || "",
            },

            labDetails: {
              labName: userData.labName || "",
              labEmail: userData.labEmail || "",
              address: userData.address || "",
              address2: userData.address2 || "",
              city: userData.city || "",
              state: userData.state || "",
              country: userData.country || "",
              zipCode: userData.zipCode || "",
              telephone: userData.telephone || "",
              subdomain: userData.subdomain || "",
            },
          }));
        } else {
          throw new Error(
            response.data.message ||
              "Failed to fetch user data"
          );
        }
      } catch (err) {
        if (err.response?.status === 401) {
          showToast(
            "Session expired. Please login again.",
            "error"
          );

          navigate("/login");
        } else {
          setError(
            err.response?.data?.message ||
              err.message ||
              "Failed to fetch user data"
          );

          showToast(
            err.response?.data?.message ||
              "Failed to fetch user data",
            "error"
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, navigate, getApiBaseUrl]);

  // =========================================================
  // INPUT CHANGE
  // =========================================================

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // =========================================================
  // AUTHENTICATION CHANGE
  // =========================================================

  const handleAuthenticationChange = (updatedAuthData) => {
    setState((prev) => ({
      ...prev,
      authentication: {
        ...prev.authentication,
        ...updatedAuthData,
      },
    }));
  };

  // =========================================================
  // SUBSCRIPTIONS CHANGE
  // =========================================================

  const handleSubscriptionsChange = (
    fieldType,
    fieldName,
    checked
  ) => {
    setState((prev) => ({
      ...prev,
      [fieldType]: {
        ...prev[fieldType],
        [fieldName]: checked,
      },
    }));
  };

  // =========================================================
  // NOTIFICATIONS CHANGE
  // =========================================================

  const handleNotificationsChange = (
    updatedNotificationsData
  ) => {
    setState((prev) => ({
      ...prev,

      emailNotifications:
        updatedNotificationsData.emailNotifications ||
        prev.emailNotifications,

      webNotifications:
        updatedNotificationsData.webNotifications ||
        prev.webNotifications,
    }));
  };

  // =========================================================
  // LAB DETAILS CHANGE
  // =========================================================

  const handleLabDetailsChange = (updatedLabDetails) => {
    setState((prev) => ({
      ...prev,

      labDetails: updatedLabDetails,

      labName: updatedLabDetails.labName,
      labEmail: updatedLabDetails.labEmail,
      address: updatedLabDetails.address,
      address2: updatedLabDetails.address2,
      city: updatedLabDetails.city,
      state: updatedLabDetails.state,
      country: updatedLabDetails.country,
      zipCode: updatedLabDetails.zipCode,
      telephone: updatedLabDetails.telephone,
      subdomain: updatedLabDetails.subdomain,
    }));
  };

  // =========================================================
  // BASIC USER SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!userId) {
        throw new Error("User ID not found in URL");
      }

      const token = localStorage.getItem("accessToken");

      if (!token) {
        throw new Error(
          "No authentication token found. Please login again."
        );
      }

      const updateData = {
        salutation: state.salutation,
        first_name: state.first_name,
        middleInitial: state.middleInitial,
        last_name: state.last_name,
        username: state.username,
        userId: state.userId,
        email: state.email,
        labEmail: state.labEmail,
        labName: state.labName,
        address: state.address,
        address2: state.address2,
        city: state.city,
        state: state.state,
        country: state.country,
        zipCode: state.zipCode,
        telephone: state.telephone,
        timeZone: state.timeZone,
        subdomain: state.subdomain,
        newsletter: state.newsletter,
        trainingInterest: state.trainingInterest,
      };

      const response = await axios.put(
        `${getApiBaseUrl()}/api/auth/${userId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast(
          "Profile updated successfully!",
          "success"
        );

        setState((prev) => ({
          ...prev,
          ...response.data.user,
        }));
      } else {
        throw new Error(
          response.data.message ||
            "Failed to update profile"
        );
      }
    } catch (err) {
      if (err.response?.status === 401) {
        showToast(
          "Session expired. Please login again.",
          "error"
        );

        navigate("/login");
      } else {
        showToast(
          err.response?.data?.message ||
            "Failed to update profile",
          "error"
        );
      }
    }
  };

  // =========================================================
  // AUTHENTICATION SUBMIT
  // =========================================================

  const handleAuthenticationSubmit = async (authData) => {
    const {
      userName,
      password,
      confirmUserName,
      confirmPassword,
      noEmail,
    } = authData;

    const updateData = {};

    if (userName && userName.trim() !== "") {
      const currentValue = noEmail
        ? state.username
        : state.email;

      if (userName !== currentValue) {
        if (noEmail) {
          updateData.username = userName;
        } else {
          const emailRegex =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

          if (!emailRegex.test(userName)) {
            showToast(
              "Please enter a valid email address.",
              "error"
            );

            return false;
          }

          updateData.email = userName;
        }
      }
    }

    if (password && password.trim() !== "") {
      if (password !== confirmPassword) {
        showToast(
          "Passwords do not match.",
          "error"
        );

        return false;
      }

      if (password.length < 4) {
        showToast(
          "Password must be at least 4 characters long.",
          "error"
        );

        return false;
      }

      updateData.password = password;
    }

    if (Object.keys(updateData).length === 0) {
      showToast(
        "No changes were made to authentication details.",
        "info"
      );

      return false;
    }

    try {
      const token =
        localStorage.getItem("accessToken");

      if (!token) {
        throw new Error(
          "No authentication token found"
        );
      }

      const response = await axios.put(
        `${getApiBaseUrl()}/api/auth/${userId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast(
          "Authentication details updated successfully!",
          "success"
        );

        if (updateData.email !== undefined) {
          localStorage.setItem(
            "userEmail",
            updateData.email
          );

          setState((prev) => ({
            ...prev,
            email: updateData.email,
            username: "",
          }));
        }

        if (updateData.username !== undefined) {
          setState((prev) => ({
            ...prev,
            username: updateData.username,
            email: "",
          }));
        }

        setState((prev) => ({
          ...prev,

          authentication: {
            ...prev.authentication,
            password: "",
            confirmPassword: "",
          },
        }));

        return true;
      } else {
        throw new Error(
          response.data.message ||
            "Failed to update authentication details"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showToast(
          "Session expired. Please login again.",
          "error"
        );

        navigate("/login");
      } else {
        showToast(
          error.response?.data?.message ||
            error.message ||
            "Failed to update authentication details",
          "error"
        );
      }

      return false;
    }
  };

  // =========================================================
  // SUBSCRIPTIONS SUBMIT
  // =========================================================

  const handleSubscriptionsSubmit = async (
    subscriptionsData
  ) => {
    try {
      const token =
        localStorage.getItem("accessToken");

      if (!token) {
        throw new Error(
          "No authentication token found"
        );
      }

      const response = await axios.put(
        `${getApiBaseUrl()}/api/auth/${userId}`,
        subscriptionsData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast(
          "Subscriptions updated successfully!",
          "success"
        );

        return true;
      } else {
        throw new Error(
          response.data.message ||
            "Failed to update subscriptions"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showToast(
          "Session expired. Please login again.",
          "error"
        );

        navigate("/login");
      } else {
        showToast(
          error.message ||
            "Failed to update subscriptions",
          "error"
        );
      }

      return false;
    }
  };

  // =========================================================
  // NOTIFICATIONS SUBMIT
  // =========================================================

  const handleNotificationsSubmit = async (
    notificationsData
  ) => {
    try {
      const token =
        localStorage.getItem("accessToken");

      if (!token) {
        throw new Error(
          "No authentication token found"
        );
      }

      const response = await axios.put(
        `${getApiBaseUrl()}/api/auth/${userId}`,
        notificationsData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast(
          "Notifications updated successfully!",
          "success"
        );

        return true;
      } else {
        throw new Error(
          response.data.message ||
            "Failed to update notifications"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showToast(
          "Session expired. Please login again.",
          "error"
        );

        navigate("/login");
      } else {
        showToast(
          error.message ||
            "Failed to update notifications",
          "error"
        );
      }

      return false;
    }
  };

  // =========================================================
  // PREFERENCES SUBMIT
  // =========================================================

  const handlePreferencesSubmit = async ({
    preferences,
  }) => {
    try {
      const token =
        localStorage.getItem("accessToken");

      if (!token) {
        throw new Error(
          "No authentication token found"
        );
      }

      const response = await axios.put(
        `${getApiBaseUrl()}/api/auth/${userId}`,
        { preferences },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast(
          "Preferences updated successfully!",
          "success"
        );

        setState((prev) => ({
          ...prev,

          preferences:
            response.data.user.preferences ||
            prev.preferences,
        }));

        return true;
      } else {
        throw new Error(
          response.data.message ||
            "Failed to update preferences"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showToast(
          "Session expired. Please login again.",
          "error"
        );

        navigate("/login");
      } else {
        const err = new Error(
          error.response?.data?.message ||
            "Failed to update preferences"
        );

        err.handled = true;

        showToast(err.message, "error");

        throw err;
      }
    }
  };

  // =========================================================
  // LAB DETAILS SUBMIT
  // =========================================================

  const handleLabDetailsSubmit = async ({
    labDetails,
  }) => {
    try {
      const token =
        localStorage.getItem("accessToken");

      if (!token) {
        throw new Error(
          "No authentication token found"
        );
      }

      const updateData = {
        labName: labDetails.labName,
        labEmail: labDetails.labEmail,
        address: labDetails.address,
        address2: labDetails.address2,
        city: labDetails.city,
        state: labDetails.state,
        country: labDetails.country,
        zipCode: labDetails.zipCode,
        telephone: labDetails.telephone,
        subdomain: labDetails.subdomain,
      };

      const response = await axios.put(
        `${getApiBaseUrl()}/api/auth/${userId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast(
          "Lab details updated successfully!",
          "success"
        );

        setState((prev) => ({
          ...prev,

          ...updateData,

          labDetails: {
            ...prev.labDetails,
            ...labDetails,
          },
        }));

        return true;
      } else {
        throw new Error(
          response.data.message ||
            "Failed to update lab details"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showToast(
          "Session expired. Please login again.",
          "error"
        );

        navigate("/login");
      } else {
        showToast(
          error.message ||
            "Failed to update lab details",
          "error"
        );
      }

      return false;
    }
  };

  // =========================================================
  // PRODUCTIVITY SUBMIT
  // =========================================================

  const handleProductivitySubmit = async (
    productivityData
  ) => {
    try {
      const token =
        localStorage.getItem("accessToken");

      if (!token) {
        throw new Error(
          "No authentication token found"
        );
      }

      const response = await axios.put(
        `${getApiBaseUrl()}/api/auth/${userId}`,
        productivityData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast(
          "Productivity updated successfully!",
          "success"
        );

        setState((prev) => ({
          ...prev,

          productivity:
            response.data.user.productivity ||
            prev.productivity,
        }));

        return true;
      } else {
        throw new Error(
          response.data.message ||
            "Failed to update productivity"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showToast(
          "Session expired. Please login again.",
          "error"
        );

        navigate("/login");
      } else {
        showToast(
          error.message ||
            "Failed to update productivity",
          "error"
        );
      }

      return false;
    }
  };

  // =========================================================
  // PERMISSION SUBMIT
  // =========================================================

  const handlePermissionSubmit = async (
    permissionData
  ) => {
    try {
      const token =
        localStorage.getItem("accessToken");

      if (!token) {
        throw new Error(
          "No authentication token found"
        );
      }

      const response = await axios.put(
        `${getApiBaseUrl()}/api/auth/${userId}`,
        permissionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        showToast(
          "Permissions updated successfully!",
          "success"
        );

        setState((prev) => ({
          ...prev,

          permissions:
            response.data.user.permissions ||
            prev.permissions,
        }));

        return true;
      } else {
        throw new Error(
          response.data.message ||
            "Failed to update permissions"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showToast(
          "Session expired. Please login again.",
          "error"
        );

        navigate("/login");
      } else {
        showToast(
          error.message ||
            "Failed to update permissions",
          "error"
        );
      }

      return false;
    }
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading && !error) {
    return (
      <div>
        <Header />
        <Navvbar />

        <div className="edit-user-loading">
          <div
            className="spinner-border text-primary"
            role="status"
          >
            <span className="visually-hidden">
              Loading...
            </span>
          </div>

          <div className="edit-user-loading-text">
            Loading user data...
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // MAIN RETURN
  // =========================================================

  return (
    <div className="edit-user-page">

      <Header />

      <Navvbar />

      {/* =====================================================
          MAIN CONTAINER
         ===================================================== */}

      <div className="edit-user-container">
        <div className="row edit-user-row">

          {/* =====================================================
              SETTINGS SIDEBAR
             ===================================================== */}

          <div className="col-md-2 edit-settings-sidebar">
            <Sidebar />
          </div>

          {/* =====================================================
              MAIN CONTENT
             ===================================================== */}

          <div className="col-md-10 edit-user-main">

            {error && (
              <div className="edit-user-error">
                Error: {error}
              </div>
            )}

            <h1 className="edit-user-heading">
              Edit User
            </h1>

            {/* =================================================
                BASIC INFORMATION
               ================================================= */}

            <form onSubmit={handleSubmit}>
              <div className="edit-basic-form-box">

                {/* Salutation */}

                <div className="edit-basic-row">
                  <label>
                    Salutation
                  </label>

                  <select
                    name="salutation"
                    value={state.salutation}
                    onChange={handleInputChange}
                  >
                    <option value="Dr">Dr</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Miss">Miss</option>
                  </select>
                </div>

                {/* First Name */}

                <div className="edit-basic-row">
                  <label>
                    First Name *
                  </label>

                  <input
                    type="text"
                    name="first_name"
                    value={state.first_name}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Middle Initial */}

                <div className="edit-basic-row">
                  <label>
                    Middle Initial
                  </label>

                  <input
                    type="text"
                    name="middleInitial"
                    value={state.middleInitial || ""}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Last Name */}

                <div className="edit-basic-row">
                  <label>
                    Last Name *
                  </label>

                  <input
                    type="text"
                    name="last_name"
                    value={state.last_name}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Username */}

                <div className="edit-basic-row">
                  <label>
                    Username *
                  </label>

                  <input
                    type="text"
                    name="username"
                    value={state.username || ""}
                    onChange={handleInputChange}
                  />
                </div>

                {/* External ID */}

                <div className="edit-basic-row">
                  <label>
                    External ID
                  </label>

                  <input
                    type="text"
                    name="userId"
                    value={state.userId || ""}
                    onChange={handleInputChange}
                  />
                </div>

              </div>

              <div className="mt-3 edit-basic-save-area">
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Save Basic Changes
                </button>
              </div>
            </form>

            {/* =================================================
                LAB DETAILS
               ================================================= */}

            <div className="mt-3">
              <EditLabDetails
                labDetails={state.labDetails}
                onChange={handleLabDetailsChange}
                onSubmit={handleLabDetailsSubmit}
                isAdmin={isAdmin}
              />
            </div>

            {/* =================================================
                AUTHENTICATION
               ================================================= */}

            <div className="mt-3">
              <EditAuthentication
                authentication={state.authentication}
                onChange={handleAuthenticationChange}
                onSubmit={handleAuthenticationSubmit}
              />
            </div>

            {/* =================================================
                SUBSCRIPTIONS
               ================================================= */}

            <div className="mt-3">
              <EditSubscriptions
                userData={{
                  email: state.email,
                  emailNotifications:
                    state.emailNotifications,
                }}
                onUpdate={handleSubscriptionsSubmit}
                onFieldChange={
                  handleSubscriptionsChange
                }
              />
            </div>

            {/* =================================================
                NOTIFICATIONS
               ================================================= */}

            <div className="mt-3">
              <EditNotifications
                notifications={{
                  emailNotifications:
                    state.emailNotifications,

                  webNotifications:
                    state.webNotifications,
                }}
                onChange={handleNotificationsChange}
                onSubmit={handleNotificationsSubmit}
              />
            </div>

            {/* =================================================
                PREFERENCES
               ================================================= */}

            <div className="mt-3">
              <EditPreferances
                preferences={state.preferences}
                onChange={(updatedPreferences) => {
                  setState((prev) => ({
                    ...prev,
                    preferences:
                      updatedPreferences,
                  }));
                }}
                onSubmit={handlePreferencesSubmit}
              />
            </div>

            {/* =================================================
                PRODUCTIVITY
               ================================================= */}

            {isAdmin && (
              <div className="mt-3">
                <EditProductivity
                  productivity={state.productivity}
                  onChange={(updatedProductivity) => {
                    setState((prev) => ({
                      ...prev,
                      productivity:
                        updatedProductivity,
                    }));
                  }}
                  onSubmit={handleProductivitySubmit}
                />
              </div>
            )}

            {/* =================================================
                PERMISSIONS
               ================================================= */}

            {isAdmin && (
              <div className="mt-3">
                <EditPermission
                  permissions={state.permissions}
                  onChange={(updatedPermissions) => {
                    setState((prev) => ({
                      ...prev,
                      permissions:
                        updatedPermissions,
                    }));
                  }}
                  onSubmit={handlePermissionSubmit}
                />
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUser;